import { useEffect, useRef, useState } from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import type { components } from "@dolfin/api-public";
import { useDolfinClient } from "@/components/DolfinProvider";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

type CurrencyResponse = components["schemas"]["CurrencyResponse"];

export interface CurrencyComboboxProps {
  /** Selected ISO 4217 code (e.g. "GBP"), or "" when nothing is chosen. */
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  className?: string;
}

// Backed by GET /currencies/available, the list invoices validate against, so pickers can't drift from it.
export function CurrencyCombobox({
  value,
  onChange,
  disabled,
  id,
  placeholder = "Select currency",
  className,
}: CurrencyComboboxProps) {
  const api = useDolfinClient();
  const [currencies, setCurrencies] = useState<CurrencyResponse[]>([]);
  const [open, setOpen] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    api.GET("/v1/currencies/available").then(({ data }) => {
      if (data) setCurrencies(data);
    });
  }, [api]);

  const selected = currencies.find((c) => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value ? (selected ? `${selected.code} - ${selected.name}` : value) : placeholder}
          </span>
          <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) min-w-72 p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search currencies..." />
          <CommandList>
            <CommandEmpty>No currency found.</CommandEmpty>
            <CommandGroup>
              {currencies.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.code} ${c.name}`}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn("mr-2 size-4", value === c.code ? "opacity-100" : "opacity-0")}
                  />
                  <span className="mr-2 font-mono text-xs text-muted-foreground">{c.code}</span>
                  <span className="truncate">{c.name}</span>
                  <span className="ml-auto pl-2 text-muted-foreground">{c.symbol}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
